import { useState, useEffect } from "react";
import axios from "axios";
import {
  XCircle,
  User,
  Phone,
  Mail,
  CheckCircle,
  MapPin,
  Building2,
  MessageSquare,
  AlertCircle,
  Briefcase,
  Calendar,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import CustomSelect from "../../../components/ui/CustomSelect";
import { Input } from "../../../components/ui/Input";
import { useNotification } from "../../../context/NotificationContext";

export default function ProspectForm({ open, onClose, onSave, prospect }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: "Personne Physique",
    lastName: "",
    firstName: "",
    organizationName: "",
    phone: "",
    email: "",
    linkedinUrl: "",
    interestNotes: "",
    legalStatus: "",
    siret: "",
    sector: "",
    address: "",
    postalCode: "",
    city: "",
    country: "France",
    position: "",
    maritalStatus: "",
    estimatedWealthBracket: "",
    needs: "",
    firstContactAt: "",
    status: "QUALIFIED",
  });
  const [errors, setErrors] = useState({});
  const { showToast } = useNotification();
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

  // Nouveaux états
  const [countryCode, setCountryCode] = useState("FR (+33)");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  useEffect(() => {
    if (open) {
      if (prospect) {
        const safeProspect = Object.fromEntries(
          Object.entries(prospect).map(([key, val]) => [key, val == null ? "" : val])
        );
        setFormData((prev) => ({
          ...prev,
          ...safeProspect,
          firstContactAt: prospect.firstContactAt ? prospect.firstContactAt.split("T")[0] : "",
        }));
      } else {
        setFormData({
          type: "Personne Physique",
          lastName: "",
          firstName: "",
          organizationName: "",
          phone: "",
          email: "",
          linkedinUrl: "",
          interestNotes: "",
          legalStatus: "",
          siret: "",
          sector: "",
          address: "",
          postalCode: "",
          city: "",
          country: "France",
          position: "",
          maritalStatus: "",
          estimatedWealthBracket: "",
          needs: "",
          firstContactAt: "",
          status: "QUALIFIED",
        });
      }
      setStep(1);
      setErrors({});
    }
  }, [prospect, open]);

  if (!open) return null;

  const validateStep1 = () => {
    const newErrors = {};
    const isPM = formData.type === "Personne Morale";

    if (isPM) {
      if (!formData.organizationName?.trim()) newErrors.organizationName = "La dénomination sociale est obligatoire.";
    } else {
      if (!formData.lastName?.trim()) newErrors.lastName = "Le nom est obligatoire.";
      if (!formData.firstName?.trim()) newErrors.firstName = "Le prénom est obligatoire.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      showToast("Veuillez remplir tous les champs obligatoires.", "error");
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "phone") {
      const prefixes = [
        { code: "FR (+33)", prefix: "+33" },
        { code: "US (+1)", prefix: "+1" },
        { code: "CA (+1)", prefix: "+1" },
        { code: "CH (+41)", prefix: "+41" },
        { code: "BE (+32)", prefix: "+32" },
        { code: "UK (+44)", prefix: "+44" },
        { code: "DE (+49)", prefix: "+49" },
        { code: "ES (+34)", prefix: "+34" },
        { code: "IT (+39)", prefix: "+39" },
      ];

      for (const p of prefixes) {
        if (value.startsWith(p.prefix)) {
          setCountryCode(p.code);
          value = value.replace(p.prefix, "").trim();
          break;
        }
      }

      value = value.replace(/[^\d\s+]/g, "");
    }

    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const searchAddress = async (query) => {
    const trimmedQuery = query?.trim() || "";
    if (trimmedQuery.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }
    try {
      const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(trimmedQuery)}&limit=5`);
      if (!response.ok) {
        throw new Error(`API Adresse a retourné une erreur ${response.status}`);
      }
      const data = await response.json();
      setAddressSuggestions(data.features || []);
      setShowAddressSuggestions(true);
    } catch (error) {
      console.error("Erreur API Adresse :", error);
      setAddressSuggestions([]);
    }
  };

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, address: value }));
    searchAddress(value);
  };

  const selectAddress = (feature) => {
    const { name, postcode, city } = feature.properties;
    setFormData((prev) => ({
      ...prev,
      address: name || "",
      postalCode: postcode || "",
      city: city || "",
      country: "France",
    }));
    setShowAddressSuggestions(false);
  };

  const handleSubmit = async () => {
    setLoading(true);

    const newErrors = {};
    const isPM = formData.type === "Personne Morale";

    if (isPM) {
      if (!formData.organizationName?.trim()) newErrors.organizationName = "La dénomination sociale est obligatoire.";
    } else {
      if (!formData.lastName?.trim()) newErrors.lastName = "Le nom est obligatoire.";
      if (!formData.firstName?.trim()) newErrors.firstName = "Le prénom est obligatoire.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      showToast("Veuillez remplir tous les champs obligatoires.", "error");
      setLoading(false);
      return;
    }

    try {
      const dataToSave = { ...formData };
      if (prospect && prospect._id) {
        dataToSave.id = prospect._id;
      } else if (prospect && prospect.id) {
        dataToSave.id = prospect.id;
      }
      
      await onSave(dataToSave);
      showToast(prospect ? "Prospect modifié avec succès" : "Nouveau prospect ajouté", "success");
      onClose();
    } catch (err) {
      console.error("Erreur lors de l'enregistrement :", err);
      showToast("Erreur lors de l'enregistrement : vérifiez les champs.", "error");
    } finally {
      setLoading(false);
    }
  };

  const isPM = formData.type === "Personne Morale";

  const steps = isPM
    ? [
        { label: "Identité", icon: <User className="w-5 h-5 mr-2" /> },
        { label: "Coordonnées", icon: <Phone className="w-5 h-5 mr-2" /> },
        { label: "Adresse", icon: <MapPin className="w-5 h-5 mr-2" /> },
        { label: "Entreprise", icon: <Building2 className="w-5 h-5 mr-2" /> },
        { label: "Besoins", icon: <MessageSquare className="w-5 h-5 mr-2" /> },
        { label: "Infos", icon: <Calendar className="w-5 h-5 mr-2" /> },
      ]
    : [
        { label: "Identité", icon: <User className="w-5 h-5 mr-2" /> },
        { label: "Coordonnées", icon: <Phone className="w-5 h-5 mr-2" /> },
        { label: "Adresse", icon: <MapPin className="w-5 h-5 mr-2" /> },
        { label: "Situation", icon: <Briefcase className="w-5 h-5 mr-2" /> },
        { label: "Besoins", icon: <MessageSquare className="w-5 h-5 mr-2" /> },
        { label: "Infos", icon: <Calendar className="w-5 h-5 mr-2" /> },
      ];

  const stepFields = {
    1: (
      <>
        <div className="col-span-full z-[100] relative mb-2">
          <label className="text-sm font-semibold text-subtle mb-1 block">Type *</label>
          <CustomSelect
            value={formData.type}
            onChange={(val) => setFormData({ ...formData, type: val })}
            options={[
              { label: "Personne Physique", value: "Personne Physique" },
              { label: "Personne Morale", value: "Personne Morale" },
            ]}
          />
        </div>
        {isPM && (
          <div>
            <label className="text-sm font-semibold text-subtle mb-1 block">Dénomination Sociale *</label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Building2 className="h-4 w-4 text-subtle" aria-hidden="true" />
              </div>
              <Input
                name="organizationName"
                value={formData.organizationName}
                onChange={handleChange}
                hasError={!!errors.organizationName}
                className={`pl-9 ${errors.organizationName ? "pr-10" : ""}`}
                placeholder="Ex: Nanthiat Finance"
              />
            </div>
            {errors.organizationName && <p className="mt-1.5 text-xs font-medium text-[var(--hp-danger-600)]">{errors.organizationName}</p>}
          </div>
        )}
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">
            {isPM ? "Nom du contact" : "Nom *"}
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <User className="h-4 w-4 text-subtle" aria-hidden="true" />
            </div>
            <Input
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              hasError={!!errors.lastName}
              className={`pl-9 ${errors.lastName ? "pr-10" : ""}`}
              placeholder="Ex: Dupont"
            />
            {errors.lastName && (
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <AlertCircle className="h-5 w-5 text-[var(--hp-danger-600)]" aria-hidden="true" />
              </div>
            )}
          </div>
          {errors.lastName && <p className="mt-1.5 text-xs font-medium text-[var(--hp-danger-600)]">{errors.lastName}</p>}
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">
            {isPM ? "Prénom du contact" : "Prénom *"}
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <User className="h-4 w-4 text-subtle" aria-hidden="true" />
            </div>
            <Input
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              hasError={!!errors.firstName}
              className={`pl-9 ${errors.firstName ? "pr-10" : ""}`}
              placeholder="Ex: Jean"
            />
            {errors.firstName && (
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <AlertCircle className="h-5 w-5 text-[var(--hp-danger-600)]" aria-hidden="true" />
              </div>
            )}
          </div>
          {errors.firstName && <p className="mt-1.5 text-xs font-medium text-[var(--hp-danger-600)]">{errors.firstName}</p>}
        </div>
      </>
    ),
    2: (
      <>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Email</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Mail className="h-4 w-4 text-subtle" aria-hidden="true" />
            </div>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              hasError={!!errors.email}
              className={`pl-9 ${errors.email ? "pr-10" : ""}`}
              placeholder="exemple@email.com"
            />
          </div>
          {errors.email && <p className="mt-1.5 text-xs font-medium text-[var(--hp-danger-600)]">{errors.email}</p>}
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Téléphone</label>
          <div className="relative flex items-center">
            <div className="absolute inset-y-0 left-0 flex items-center">
              <select
                name="country"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="h-full rounded-md border-0 bg-transparent py-0 pl-3 text-subtle focus:ring-2 focus:ring-inset focus:ring-accent sm:text-sm w-[115px] outline-none"
              >
                <option value="FR (+33)">FR (+33)</option>
                <option value="US (+1)">US (+1)</option>
                <option value="CA (+1)">CA (+1)</option>
                <option value="CH (+41)">CH (+41)</option>
                <option value="BE (+32)">BE (+32)</option>
                <option value="UK (+44)">UK (+44)</option>
                <option value="DE (+49)">DE (+49)</option>
                <option value="ES (+34)">ES (+34)</option>
                <option value="IT (+39)">IT (+39)</option>
              </select>
            </div>
            <Input
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="pl-[125px]"
              placeholder="6 12 34 56 78"
            />
          </div>
        </div>
        <div className="col-span-full">
          <label className="text-sm font-semibold text-subtle mb-1 block">LinkedIn</label>
          <div className="flex rounded-md shadow-sm">
            <span className="inline-flex items-center rounded-l-md border border-r-0 border-workspace px-3 text-subtle sm:text-sm bg-surface-muted">
              https://
            </span>
            <Input
              name="linkedinUrl"
              value={formData.linkedinUrl}
              onChange={handleChange}
              className="rounded-l-none"
              placeholder="linkedin.com/in/profil"
            />
          </div>
        </div>
      </>
    ),
    3: (
      <>
        <div className="col-span-full relative">
          <label className="text-sm font-semibold text-subtle mb-1 block">Adresse</label>
          <Input
            name="address"
            value={formData.address}
            onChange={handleAddressChange}
            hasError={!!errors.adresse}
            className={`${errors.adresse ? "pr-10" : ""}`}
            placeholder="Saisissez une adresse..."
          />
          {showAddressSuggestions && addressSuggestions?.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm border border-workspace">
              {addressSuggestions.map((feature, idx) => (
                <li
                  key={idx}
                  onClick={() => selectAddress(feature)}
                  className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-ink-900 hover:bg-accent-50 hover:text-accent-900"
                >
                  <span className="block truncate">{feature.properties.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Code Postal</label>
          <Input name="postalCode" value={formData.postalCode} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Ville</label>
          <Input name="city" value={formData.city} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Pays</label>
          <Input name="country" value={formData.country} onChange={handleChange} />
        </div>
      </>
    ),
    4: isPM ? (
      <>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Statut Juridique</label>
          <Input name="legalStatus" value={formData.legalStatus || ""} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">SIRET</label>
          <Input name="siret" value={formData.siret} onChange={handleChange} />
        </div>
        <div className="col-span-full">
          <label className="text-sm font-semibold text-subtle mb-1 block">Secteur d’activité</label>
          <Input name="sector" value={formData.sector} onChange={handleChange} />
        </div>
      </>
    ) : (
      <>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Profession</label>
          <Input name="position" value={formData.position} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Situation Matrimoniale</label>
          <Input name="maritalStatus" value={formData.maritalStatus} onChange={handleChange} />
        </div>
      </>
    ),
    5: (
      <>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Enveloppe</label>
          <div className="relative">
            <Input
              type="text"
              name="estimatedWealthBracket"
              value={formData.estimatedWealthBracket}
              onChange={handleChange}
              className="pr-10"
              placeholder="Ex: 500k - 1M"
            />
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <span className="text-muted sm:text-sm">€</span>
            </div>
          </div>
        </div>
        <div className="col-span-full">
          <label className="text-sm font-semibold text-subtle mb-1 block">Besoins</label>
          <textarea
            name="needs"
            value={formData.needs}
            onChange={handleChange}
            rows={3}
            className="flex w-full rounded-[var(--hp-radius-md)] border border-[var(--hp-color-border)] bg-[var(--hp-color-surface)] px-3 py-2 text-sm text-[var(--hp-color-text)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hp-color-focus)] resize-none mt-1"
          />
        </div>
        <div className="col-span-full">
          <label className="text-sm font-semibold text-subtle mb-1 block">Commentaire</label>
          <textarea
            name="interestNotes"
            value={formData.interestNotes}
            onChange={handleChange}
            rows={3}
            className="flex w-full rounded-[var(--hp-radius-md)] border border-[var(--hp-color-border)] bg-[var(--hp-color-surface)] px-3 py-2 text-sm text-[var(--hp-color-text)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hp-color-focus)] resize-none mt-1"
          />
        </div>
      </>
    ),
    6: (
      <>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Date du 1er contact</label>
          <Input
            type="date"
            name="firstContactAt"
            value={formData.firstContactAt}
            onChange={handleChange}
          />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Source du contact</label>
          <CustomSelect
            value={formData.source}
            onChange={(val) => handleChange({ target: { name: 'source', value: val } })}
            placeholder="-- Sélectionner --"
            options={[
              { label: "Site web", value: "Site web" },
              { label: "LinkedIn", value: "LinkedIn" },
              { label: "Recommandation", value: "Recommandation" },
              { label: "Événement", value: "Événement" },
              { label: "Autre", value: "Autre" }
            ]}
          />
        </div>
      </>
    ),
  };

  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.4)] backdrop-blur-sm z-[60] p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="relative workspace-card-elevated w-full max-w-3xl p-8 rounded-2xl max-h-[90vh] overflow-y-auto flex flex-col"
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-6 right-6 workspace-icon-button">
          <XCircle className="w-5 h-5" />
        </button>

        <h2 className="text-2xl font-bold text-primary flex items-center mb-6">
          {steps[step - 1].icon}
          Étape {step} : {steps[step - 1].label}
        </h2>

        <div className="w-full bg-surface-muted rounded-full h-2 mb-8 overflow-hidden">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${(step / steps.length) * 100}%` }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1">
          {stepFields[step]}
        </div>

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-workspace">
          <button
            disabled={step === 1}
            onClick={() => setStep((s) => s - 1)}
            className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Précédent
          </button>

          {step < steps.length ? (
            <button
              onClick={() => {
                if (step === 1 && !validateStep1()) return;
                setStep((s) => s + 1);
              }}
              className="btn btn-primary"
            >
              Suivant
            </button>
          ) : (
            <button
              onClick={() => setShowConfirmSubmit(true)}
              disabled={loading}
              className="btn btn-primary bg-success-600 border-success-600 hover:bg-success-700 hover:border-success-700 text-white"
            >
              {loading ? "Enregistrement..." : prospect ? "Modifier le prospect" : "Créer le prospect"}
            </button>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {showConfirmSubmit && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.5)] backdrop-blur-sm z-[70] p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="workspace-card-elevated w-full max-w-sm p-6 flex flex-col items-center text-center"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="w-12 h-12 rounded-full bg-success-100 flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-success-600" />
              </div>
              <h3 className="text-lg font-bold text-primary mb-2">Confirmer l'enregistrement</h3>
              <p className="text-subtle text-sm mb-6">
                Êtes-vous sûr de vouloir {prospect ? "modifier" : "ajouter"} ce prospect ?
              </p>
              <div className="flex w-full gap-3">
                <button
                  className="btn btn-secondary flex-1"
                  onClick={() => setShowConfirmSubmit(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-primary bg-success-600 border-success-600 hover:bg-success-700 hover:border-success-700 text-white flex-1"
                  onClick={() => {
                    setShowConfirmSubmit(false);
                    handleSubmit();
                  }}
                  disabled={loading}
                >
                  {loading ? "En cours..." : "Confirmer"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
